<template>
  <b-card no-body class="m-0">
    <b-row class="d-flex justify-content-between align-items-center px-1 pt-1">
      <b-col>
        <h4 class="mb-0">Brands</h4>
        <small class="text-muted">Name, logo, description, and website shown on your ads.</small>
      </b-col>
      <b-col cols="auto">
        <b-button variant="primary" @click="openCreate">
          Add Brand
        </b-button>
      </b-col>
    </b-row>

    <div class="m-2">
      <b-table
        :busy="loading"
        :items="rows"
        :fields="fields"
        responsive
        show-empty
        empty-text="No brands yet. Add one before you create an ad."
      >
        <template #table-busy>
          <div class="text-center text-secondary my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>
        <template #cell(logo)="data">
          <b-avatar
            :src="data.item.logo || undefined"
            :text="data.item.logo ? '' : initial(data.item.name)"
            size="40"
            variant="light"
          />
        </template>
        <template #cell(website)="data">
          <b-link
            v-if="data.item.website"
            :href="data.item.website"
            target="_blank"
            rel="noopener"
          >
            {{ data.item.website }}
          </b-link>
          <span v-else>—</span>
        </template>
        <template #cell(is_active)="data">
          <b-badge :variant="data.item.is_active ? 'light-success' : 'light-secondary'">
            {{ data.item.is_active ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <row-actions
            :delete-disabled="data.item.ads_count > 0"
            :delete-title="data.item.ads_count > 0 ? 'This brand has ads and cannot be deleted' : 'Delete'"
            @edit="openEdit(data.item)"
            @delete="remove(data.item)"
          />
        </template>
      </b-table>
    </div>

    <b-modal
      v-model="modalOpen"
      :title="editingId ? 'Edit brand' : 'Add brand'"
      centered
      hide-footer
      @hidden="resetForm"
    >
      <b-form @submit.prevent="save">
        <label class="field-label">Name *</label>
        <b-form-input v-model="form.name" required maxlength="255" class="mb-1" />

        <label class="field-label">Logo</label>
        <div class="d-flex align-items-center mb-1">
          <b-avatar :src="form.logo || undefined" size="48" class="mr-1" />
          <b-form-file
            accept="image/png,image/jpeg,image/webp"
            placeholder="Upload logo"
            :disabled="uploading"
            @input="onLogo"
          />
        </div>

        <label class="field-label">Description</label>
        <b-form-textarea v-model="form.description" rows="3" maxlength="5000" class="mb-1" />

        <label class="field-label">Website</label>
        <b-form-input v-model="form.website" type="url" placeholder="https://example.com" class="mb-1" />

        <b-form-checkbox v-model="form.is_active" class="mb-1">
          Active
        </b-form-checkbox>

        <div class="d-flex justify-content-end">
          <b-button variant="outline-secondary" class="mr-50" @click="modalOpen = false">
            Cancel
          </b-button>
          <b-button type="submit" variant="primary" :disabled="saving || uploading">
            {{ saving ? 'Saving…' : 'Save' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BForm,
  BFormCheckbox,
  BFormFile,
  BFormInput,
  BFormTextarea,
  BLink,
  BModal,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import RowActions from '@/components/RowActions.vue'
import { confirmAction, notifyError, notifySuccess } from '@/utils/uiActions'

const emptyForm = () => ({
  name: '',
  logo: '',
  description: '',
  website: '',
  is_active: true,
})

export default {
  components: {
    RowActions,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCol,
    BForm,
    BFormCheckbox,
    BFormFile,
    BFormInput,
    BFormTextarea,
    BLink,
    BModal,
    BRow,
    BSpinner,
    BTable,
  },
  data() {
    return {
      loading: false,
      saving: false,
      uploading: false,
      modalOpen: false,
      editingId: null,
      rows: [],
      form: emptyForm(),
      fields: [
        { key: 'logo', label: 'Logo' },
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
        { key: 'website', label: 'Website' },
        { key: 'ads_count', label: 'Ads' },
        { key: 'is_active', label: 'Status' },
        { key: 'actions', label: '' },
      ],
    }
  },
  mounted() {
    this.fetchBrands()
  },
  methods: {
    initial(name) {
      return String(name || '?').trim().charAt(0).toUpperCase()
    },
    async fetchBrands() {
      this.loading = true
      try {
        const response = await vendorPanelApi.brands()
        this.rows = response?.data?.data?.brands || []
      } catch (error) {
        this.rows = []
        notifyError(this, error?.response?.data?.message || 'Unable to load brands')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editingId = null
      this.form = emptyForm()
      this.modalOpen = true
    },
    openEdit(brand) {
      this.editingId = brand.id
      this.form = {
        name: brand.name || '',
        logo: brand.logo || '',
        description: brand.description || '',
        website: brand.website || '',
        is_active: brand.is_active !== false,
      }
      this.modalOpen = true
    },
    resetForm() {
      this.editingId = null
      this.form = emptyForm()
    },
    async onLogo(file) {
      if (!file) return
      this.uploading = true
      try {
        this.form.logo = await vendorPanelApi.uploadAdMediaFile(file)
      } catch (error) {
        notifyError(this, error?.response?.data?.message || error?.message || 'Unable to upload logo', 'Upload failed')
      } finally {
        this.uploading = false
      }
    },
    async save() {
      const name = String(this.form.name || '').trim()
      if (!name) {
        notifyError(this, 'Enter a brand name.', 'Name required')
        return
      }
      const website = String(this.form.website || '').trim()
      if (website && !/^https?:\/\//i.test(website)) {
        notifyError(this, 'Website must start with http:// or https://', 'Invalid website')
        return
      }
      const payload = {
        name,
        logo: this.form.logo || null,
        description: String(this.form.description || '').trim() || null,
        website: website || null,
        is_active: Boolean(this.form.is_active),
      }
      this.saving = true
      try {
        if (this.editingId) {
          await vendorPanelApi.updateBrand(this.editingId, payload)
          notifySuccess(this, 'Brand updated successfully.', 'Updated')
        } else {
          await vendorPanelApi.createBrand(payload)
          notifySuccess(this, 'Brand created successfully.', 'Created')
        }
        this.modalOpen = false
        await this.fetchBrands()
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.response?.data?.errors?.website?.[0]
          || 'Unable to save brand'
        notifyError(this, message)
      } finally {
        this.saving = false
      }
    },
    remove(brand) {
      if (brand.ads_count > 0) return
      confirmAction(
        this,
        {
          title: `Delete ${brand.name}?`,
          text: 'This brand will be removed.',
          confirmButtonText: 'Delete',
        },
        async () => {
          try {
            await vendorPanelApi.deleteBrand(brand.id)
            notifySuccess(this, 'Brand deleted successfully.', 'Deleted')
            await this.fetchBrands()
          } catch (error) {
            notifyError(this, error?.response?.data?.message || 'Unable to delete brand')
          }
        },
      )
    },
  },
}
</script>
